import React, { useMemo, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Box, Grid2 as Grid, Stack } from '@mui/material';
import {
  CellProps,
  CellType,
  CollapsibleRowGridCellInfo,
  CustomGridCellInfo,
  EDITABLE_LIST_COLLAPSIBLE_ROW,
  EDITABLE_LIST_CUSTOM,
  EDITABLE_LIST_FIELD,
  EDITABLE_LIST_GROUP,
  EDITABLE_LIST_NESTED_LIST,
  FieldGridCellInfo,
  GridCellInfo,
  GroupGridCellInfo,
  NestedListGridCellInfo,
  NEW_ROW_ID_PREFIX,
} from './types';
import { ShowContextProvider, ShowData, useShowContext } from 'framework/show';
import { useListFormShowContext } from './EditableListFormShowContext';
import MinusIcon from '../../../ui/mui-builder/AppIcon/Icons/MinusIcon';
import Add from '@mui/icons-material/Add';
import { useFormContext, useFormShowContext } from 'framework/form';
import { ListItem, useListContext } from '../../ListContext';
import EditableListFormShowTemplate from './EditableListFormShowTemplate';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { get } from 'lodash';
import { useUiTheme } from 'framework/uiTheme';
import { Button } from '../../../ui/mui-builder/Button/Button';

const CustomCell = ({ cellInfo, isRowEnabled }: CellProps<CustomGridCellInfo<any>>) => {
  return (
    <cellInfo.component
      {...((cellInfo.componentProps as any) || {})}
      disabled={cellInfo.componentProps?.disabled || !isRowEnabled}
    />
  );
};

const FieldCell = ({ cellInfo, rowIndex, isRowEnabled }: CellProps<FieldGridCellInfo<any>>) => {
  const { formSource } = useListFormShowContext();
  const { data: visibleData } = useShowContext();
  const { data: editableData } = useFormContext();

  const baseSource = cellInfo.isEditor ? `${formSource}.${rowIndex}` : '';
  const source = cellInfo.isEditor ? `${baseSource}.${cellInfo.source}` : cellInfo.source;

  const isVisible = useMemo(() => {
    if (!cellInfo.isVisible) {
      return true;
    }

    if (cellInfo.isEditor) {
      return cellInfo.isVisible(get(editableData, baseSource));
    } else {
      return cellInfo.isVisible(visibleData);
    }
  }, [baseSource, cellInfo, editableData, visibleData]);

  if (!isVisible) {
    return null;
  }

  return (
    <cellInfo.component
      {...((cellInfo.componentProps as any) || {})}
      source={source}
      disabled={cellInfo.componentProps?.disabled || !isRowEnabled}
    />
  );
};

const GroupCell = ({ cellInfo, rowIndex, isRowEnabled }: CellProps<GroupGridCellInfo<any>>) => {
  return (
    <cellInfo.component {...((cellInfo.componentProps as any) || {})}>
      {cellInfo.children?.map((cell, subcellIndex) => (
        <Cell
          cellInfo={cell}
          isRowEnabled={isRowEnabled}
          rowIndex={rowIndex}
          cellIndex={subcellIndex}
          // eslint-disable-next-line react/no-array-index-key
          key={`group-cell-${subcellIndex}`}
        />
      ))}
    </cellInfo.component>
  );
};

const NestedListRows = ({ cellInfo, rowIndex, cellIndex, isRowEnabled }: CellProps<NestedListGridCellInfo<any>>) => {
  const { data: { items = [] } = {}, addToList, deleteFromList } = useListContext();
  const {
    enableUpdateCount = false,
    newRowIdPrefix = NEW_ROW_ID_PREFIX,
    rowKeySource = 'id',
    addNewItemButtonText = 'Add item',
    emptyListComponent = <></>,
  } = cellInfo.rows || {};
  const { isEditMode } = useFormShowContext();
  const isEditButtonsEnabled = isEditMode && isRowEnabled && enableUpdateCount;
  const { Button: ThemedButton = Button } = useUiTheme();

  const DeleteRowButton = () => {
    const { data } = useShowContext();
    return (
      <ThemedButton
        aria-label="delete"
        variant="contained"
        sx={{ padding: '0px', minWidth: 0, mt: 1 }}
        onClick={() => deleteFromList?.(data.getValueBySource(rowKeySource))}
      >
        <MinusIcon sx={{ color: 'common.white' }} />
      </ThemedButton>
    );
  };

  return (
    <>
      {items.length > 0 && (
        <Stack>
          {items.map((item: ListItem, subRowIndex: number) => {
            const dataInstance = new ShowData(item);
            return (
              <ShowContextProvider
                value={{
                  data: dataInstance,
                }}
              >
                <GroupCell
                  key={dataInstance.getValueBySource(rowKeySource)}
                  cellInfo={{
                    ...cellInfo,
                    type: EDITABLE_LIST_GROUP,
                    children: [
                      ...(cellInfo.children || []),
                      ...(isEditButtonsEnabled
                        ? [
                            {
                              type: EDITABLE_LIST_CUSTOM,
                              component: DeleteRowButton,
                              gridCellProps: {
                                size: 0.3,
                              },
                            },
                          ]
                        : []),
                    ],
                  }}
                  rowIndex={subRowIndex}
                  cellIndex={cellIndex}
                  isRowEnabled={isRowEnabled}
                />
              </ShowContextProvider>
            );
          })}
        </Stack>
      )}
      {items.length === 0 && emptyListComponent}
      {isEditButtonsEnabled && (
        <Box mt={1} key="add-to-nested-list-button">
          <ThemedButton
            variant="outlined"
            size="small"
            onClick={() => addToList?.({ id: `${newRowIdPrefix}${items.length + 1}` })}
          >
            <Add sx={{ fontSize: '0.8rem' }} />
            {addNewItemButtonText}
          </ThemedButton>
        </Box>
      )}
    </>
  );
};

const NestedListCell = ({ cellInfo, ...props }: CellProps<NestedListGridCellInfo<any>>) => {
  return (
    <EditableListFormShowTemplate listSource={cellInfo.source} rowNumber={props.rowIndex}>
      <NestedListRows {...props} cellInfo={cellInfo} />
    </EditableListFormShowTemplate>
  );
};

const CollapsibleRowCell = ({
  cellInfo: { defaultExpanded = false, expandIcon = <ExpandMoreIcon />, title, details = [] },
  rowIndex,
  cellIndex,
  isRowEnabled,
}: CellProps<CollapsibleRowGridCellInfo>) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const handleChange = (event: any, isExpanded: boolean) => {
    const isExpandIconClick = event.target.closest('.MuiAccordionSummary-expandIconWrapper');

    if (isExpandIconClick) {
      setExpanded(isExpanded);
    }
  };

  const handleSummaryClick = (event: any) => {
    if (!event.target.closest('.MuiAccordionSummary-expandIconWrapper')) {
      event.stopPropagation();
    }
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={handleChange}
      data-testid="project-section-accordion"
      sx={{
        background: 'transparent',
      }}
    >
      <AccordionSummary
        expandIcon={expandIcon}
        onClick={handleSummaryClick}
        aria-controls="panel1a-content"
        sx={{
          p: 0,
          m: 0,
          fontSize: '14px',
          fontFamily: `var(--open-sans)`,
          flexDirection: 'row-reverse',
          '& .MuiAccordionSummary-content': {
            pointerEvents: 'auto',
            alignItems: 'center',
            justifyContent: 'space-between',
            m: 0,
            '&.Mui-expanded': {
              m: 0,
            },
          },
        }}
      >
        {title.map(c => (
          <Cell cellInfo={c} rowIndex={rowIndex} cellIndex={cellIndex} isRowEnabled={isRowEnabled} />
        ))}
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        {details.map(c => (
          <Cell cellInfo={c} rowIndex={rowIndex} cellIndex={cellIndex} isRowEnabled={isRowEnabled} />
        ))}
      </AccordionDetails>
    </Accordion>
  );
};

const Cell = ({ cellInfo, rowIndex, cellIndex, isRowEnabled }: CellProps<GridCellInfo<any>>) => {
  const cellComponents: Record<CellType, React.ComponentType<CellProps<any>>> = {
    [EDITABLE_LIST_FIELD]: FieldCell,
    [EDITABLE_LIST_GROUP]: GroupCell,
    [EDITABLE_LIST_CUSTOM]: CustomCell,
    [EDITABLE_LIST_NESTED_LIST]: NestedListCell,
    [EDITABLE_LIST_COLLAPSIBLE_ROW]: CollapsibleRowCell,
  };

  const Component = cellComponents[cellInfo.type];

  return (
    <Grid {...(cellInfo.gridCellProps || {})} key={`${rowIndex}-${cellIndex}`}>
      <Component
        cellInfo={cellInfo}
        rowIndex={rowIndex}
        cellIndex={cellIndex}
        isRowEnabled={isRowEnabled}
        key={`${rowIndex}-${cellIndex}`}
      />
    </Grid>
  );
};

export default Cell;
